"use client";

import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { mutationOptions, useMutation } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { courseManagementApi } from "../api";
import type { Course, UpdateCourseInput } from "../types";

interface EditCourseDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	course: Course;
}

const editCourseSchema = z.object({
	name: z
		.string()
		.min(1, "Course name is required")
		.max(100, "Course name must not exceed 100 characters"),
	autoFire: z.boolean(),
});

type EditCourseFormValues = z.infer<typeof editCourseSchema>;

export function EditCourseDialog({
	open,
	onOpenChange,
	tenant,
	course,
}: EditCourseDialogProps) {
	const mutation = useMutation(
		mutationOptions({
			...courseManagementApi.updateCourseOptions(tenant, course.id),
			onSuccess: (result, _variables, _onMutateResult, context) => {
				if (result.success) {
					toast.success("Course updated successfully.");
					context.client.invalidateQueries({
						queryKey: [tenant, "courses"],
					});
					handleClose(false);
				} else {
					const errorDetails = result.details as
						{ detail?: string } | undefined;
					toast.error(
						errorDetails?.detail ||
							"Failed to update course. Please try again.",
					);
				}
			},
			onError: () => {
				toast.error("Unable to update course. Please try again.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			name: course.name,
			autoFire: course.autoFire,
		} as EditCourseFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: editCourseSchema,
		},
		onSubmit: async ({ value }) => {
			const input: UpdateCourseInput = {};

			if (value.name !== course.name) {
				input.name = value.name.trim();
			}

			if (value.autoFire !== course.autoFire) {
				input.autoFire = value.autoFire;
			}

			if (Object.keys(input).length === 0) {
				handleClose(false);
				return;
			}

			await mutation.mutateAsync(input);
		},
	});

	React.useEffect(() => {
		if (open) {
			form.reset({
				name: course.name,
				autoFire: course.autoFire,
			});
		}
	}, [course, open, form]);

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit Course</DialogTitle>
					<DialogDescription>
						Update the course name or auto-fire setting. Changes only affect new
						orders.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to update course. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Course Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Starters, Mains, Desserts"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="autoFire">
						{(field) => (
							<field.FormItem>
								<div className="flex items-center space-x-2">
									<Checkbox
										id="edit-autoFire"
										checked={field.state.value}
										onCheckedChange={(checked) =>
											field.handleChange(checked === true)
										}
									/>
									<Label htmlFor="edit-autoFire" className="text-sm">
										Auto-fire
									</Label>
								</div>
								<field.FormDescription>
									When enabled, items in this course are sent automatically when
									the order is saved. Changes only affect new orders.
								</field.FormDescription>
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Save Changes
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
